import {Injectable} from '@angular/core';
import {Slot} from '@app/review/models';
import {BehaviorSubject, merge, Observable} from 'rxjs';

// A view is [zoom, panX, panY] - zoom relative to the asset's own fit-to-window size, pan as a fraction of
// the current on-screen size - rather than an absolute pane-pixel matrix, so it means the same thing
// regardless of which asset/pane bounds it's applied against (needed once a locked compare mirrors this
// between two differently-sized Assets - see docs/design/review-compare-assets.md §3.5). The asset/bounds-
// aware matrix math lives entirely in ReviewImageComponent, which is the only place that has both.
export class SlottedView {
    view: number[] = [1, 0, 0];
    slot: Slot = Slot.Primary;
}

// Keyed by Slot (Primary/Compare), same rationale as ReviewVideoService - see
// docs/design/review-compare-assets.md §3.2.
//
// One BehaviorSubject per slot (not one shared subject tagged by slot) - a shared subject only remembers
// whichever slot published *last*, so a freshly-mounted pane for the *other* slot replays a value tagged
// for a slot it isn't, gets filtered out by ReviewImageComponent's `vm$`, and renders nothing until
// something happens to publish for its own slot again. Since the active pane deliberately never publishes
// locally (ReviewImageComponent.notifyTransform() - publishing there would re-commit `main` mid-drag and
// double the pan, see that method's comment), that could be indefinitely - merging two per-slot subjects
// means a new subscriber always immediately gets both slots' current values, filtered to whichever it asks
// for, regardless of which slot most recently published.
@Injectable({
    providedIn: 'root',
})
export class ReviewImageService {
    public transform$: Observable<SlottedView>;

    private _primarySubject = new BehaviorSubject<SlottedView>({view: [1, 0, 0], slot: Slot.Primary});
    private _compareSubject = new BehaviorSubject<SlottedView>({view: [1, 0, 0], slot: Slot.Compare});

    constructor() {
        this.transform$ = merge(this._primarySubject, this._compareSubject);
    }

    public setTransform(view: number[], slot: Slot = Slot.Primary): void {
        const subject = slot === Slot.Compare ? this._compareSubject : this._primarySubject;
        subject.next({view, slot});
    }
}
